// src/components/Header.js (enhanced)
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Hospital, Activity, Users, ArrowLeft, Plus,
  BarChart3, Settings, LogOut, User, Bell
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './Header.css';

const Header = ({ currentView, onNavigate, onBack, onAddPatient }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications] = useState(3); // Mock notification count

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
    { id: 'patients', label: 'Patients', icon: Users },
    { id: 'reports', label: 'Reports', icon: BarChart3 }
  ];

  const { logout } = useAuth();

  async function handleLogout() {
    try {
      await logout();
      // Redirect is handled by private route in App.js
    } catch {
      console.error("Failed to log out");
    }
  }

  const userMenuItems = [
    { label: 'Profile', icon: User, action: () => { } },
    { label: 'Settings', icon: Settings, action: () => { } },
    { label: 'Logout', icon: LogOut, action: handleLogout }
  ];

  return (
    <header className="header">
      <div className="header-content">
        {/* Left Section - Logo and Back Button */}
        <div className="header-left">
          {onBack && (
            <motion.button
              className="back-btn"
              onClick={onBack}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeft size={20} />
            </motion.button>
          )}

          <motion.div
            className="logo"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
            onClick={() => onNavigate('dashboard')}
          >
            <Hospital size={32} className="logo-icon" />
            <div className="logo-text">
              <span className="logo-primary">Vaccine</span>
              <span className="logo-secondary">Tracker</span>
            </div>
          </motion.div>
        </div>

        {/* Center Section - Navigation */}
        <nav className="navigation">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <motion.button
                key={item.id}
                className={`nav-btn ${currentView === item.id ? 'active' : ''}`}
                onClick={() => onNavigate(item.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon size={20} />
                <span>{item.label}</span>
                {currentView === item.id && (
                  <motion.div
                    className="nav-indicator"
                    layoutId="nav-indicator"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </nav>

        {/* Right Section - Actions and User Menu */}
        <div className="header-right">
          {/* Add Patient Button */}
          {currentView === 'patients' && (
            <motion.button
              className="add-patient-btn"
              onClick={onAddPatient}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus size={20} />
              <span>Add Patient</span>
            </motion.button>
          )}

          {/* Notifications */}
          <motion.button
            className="notification-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Bell size={20} />
            {notifications > 0 && (
              <span className="notification-badge">{notifications}</span>
            )}
          </motion.button>

          {/* User Menu */}
          <div className="user-menu-container">
            <motion.button
              className="user-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="user-avatar">
                <User size={20} />
              </div>
              <span className="user-name">Dr. Rupesh Shrestha</span>
            </motion.button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  className="user-dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {userMenuItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <motion.button
                        key={item.label}
                        className="dropdown-item"
                        onClick={() => {
                          item.action && item.action();
                          setShowUserMenu(false);
                        }}
                      >
                        <Icon size={16} />
                        <span>{item.label}</span>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Progress Bar for Page Loading */}
      <motion.div
        className="progress-bar"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 2, ease: "easeInOut" }}
      />
    </header>
  );
};

export default Header;